import { createContext, useContext } from 'react';

/* ─────────────────────────────────────────────────────
   Proposal theming (V4 client proposal page)
   Per-org branding is delivered at runtime via React context
   so the ~8 module-level helpers and the main component body
   all read the same theme. Defaults = the original V4 light
   professional palette, so any consumer that renders before
   data loads still shows valid colors (never a half-themed page).
   ───────────────────────────────────────────────────── */

/* "#21436b" -> "33,67,107" (handles 3- or 6-digit hex). Used to build the
   navy-tinted rgba() literals (rings, faint shadows) so they track the org's
   primary color instead of staying hardcoded navy. Falls back to the V4 navy
   components when the input isn't a parseable hex. */
export function hexToRgb(hex) {
  const fallback = '33,67,107';
  if (typeof hex !== 'string') return fallback;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return fallback;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

/* V4 light professional defaults (navy heading, sage-green accent, white bg) */
export const V4_DEFAULTS = {
  bg: '#FFFFFF',
  bgCard: '#FFFFFF',
  bgCardHover: '#F3F4F6',
  gold: '#5b8c52',
  goldLight: '#6a9e60',
  goldDark: '#4a7a43',
  heading: '#21436b',
  // RGB components of `heading`, for composing rgba() tints (rings, soft shadows).
  headingRgb: '33,67,107',
  white: '#FFFFFF',
  gray100: '#F5F5F5',
  gray400: 'rgba(33,67,107,0.7)',
  gray600: 'rgba(33,67,107,0.55)',
  gray800: '#E5E7EB',
  border: 'rgba(33,67,107,0.12)',
  borderSubtle: '#e6edf1',
  green: '#059669',
  amber: '#D97706',
  coral: '#EF4444',
};

/**
 * Build a full Z-shaped theme object from an org_branding payload.
 * Only the 4 themeable slots are swapped; every utility/status color
 * keeps its V4 default. secondary_color drives gold + its light/dark
 * variants (set equal, per spec).
 */
export function buildTheme(orgBranding) {
  if (!orgBranding) return { ...V4_DEFAULTS };
  const secondary = orgBranding.secondary_color || V4_DEFAULTS.gold;
  const heading = orgBranding.primary_color || V4_DEFAULTS.heading;
  return {
    ...V4_DEFAULTS,
    bg: orgBranding.background_color || V4_DEFAULTS.bg,
    bgCard: orgBranding.card_background_color || V4_DEFAULTS.bgCard,
    gold: secondary,
    goldLight: secondary,
    goldDark: secondary,
    heading,
    headingRgb: hexToRgb(heading),
    // Body / paragraph (secondary) text. Drives the gray400 token used for all
    // descriptive copy; falls back to the muted-navy default when unset.
    gray400: orgBranding.secondary_text_color || V4_DEFAULTS.gray400,
  };
}

export const ProposalThemeContext = createContext(V4_DEFAULTS);

export const useProposalTheme = () => useContext(ProposalThemeContext);
